import { useState } from 'react';
import { AlertTriangle, Package, RefreshCw, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/toast';

export const InactiveInventoryDialog = ({ isOpen, onClose, daysInactive, itemCount }) => {
  const { reactivateInventory } = useAuth();
  const [reactivating, setReactivating] = useState(false);

  if (!isOpen) return null;

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const result = await reactivateInventory();
      toast.success(`${result.items_reactivated} item(s) reactivated! Your inventory is now visible to buyers.`);
      onClose();
    } catch (error) {
      toast.error('Failed to reactivate inventory');
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Welcome Back!</h2>
                <p className="text-sm text-white/80">Your inventory needs attention</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6">
            <Package className="w-8 h-8 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-800">
                Your inventory has been <strong>inactive for {daysInactive} days</strong>.
              </p>
              <p className="text-sm text-amber-700 mt-1">
                <strong>{itemCount} item{itemCount !== 1 ? 's' : ''}</strong> {itemCount !== 1 ? 'are' : 'is'} currently hidden from buyers.
              </p>
            </div>
          </div>

          <p className="text-[#4A5568] text-sm mb-6">
            To keep WANTD a marketplace with active sellers, we hide inventory from users who haven't logged in recently. 
            Would you like to reactivate your items and make them visible again?
          </p>

          <div className="space-y-3">
            <Button 
              onClick={handleReactivate}
              disabled={reactivating}
              className="w-full bg-[#059669] hover:bg-[#047857] rounded-full h-12"
              data-testid="reactivate-inventory-btn"
            >
              {reactivating ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Yes, Reactivate My Inventory
                </>
              )}
            </Button>

            <Button 
              onClick={onClose}
              variant="outline"
              className="w-full border-[#E5DDD3] rounded-full h-12"
            >
              Not Now
            </Button>
          </div>

          <p className="text-xs text-[#9CA3AF] text-center mt-4">
            You can always reactivate your items later from My Items page.
          </p>
        </div>
      </div>
    </div>
  );
};
